import type { FlatSurface, PlacementPosition, SizeMm } from '@repo/api-contracts';
import { type PointerEvent, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  clientToPhotoPoint,
  type PhotoDimensions,
  type Rect,
  rectFromSurfacePoints,
  surfacePhotoToMm,
} from '@/entities/edit-area';

export type DrawAreaKind = 'rect' | 'ellipse';

interface Options {
  svgRef: RefObject<SVGSVGElement | null>;
  photoSize: PhotoDimensions;
  /** 그릴 평면 표면. 없으면 그리지 않는다. */
  surface: FlatSurface | undefined;
  /** 켜진 도형 도구. null 이면 그리지 않고, 끄는 중이던 것도 버린다. */
  kind: DrawAreaKind | null;
  /** 놓은 사각형(표면 mm). 거부 사유를 돌려주면 그 사유를 보인다. */
  onCreate: (kind: DrawAreaKind, rect: Rect) => string | null;
  /** Esc 로 그리기를 그만둘 때. */
  onCancel: () => void;
}

interface Drag {
  readonly pointerId: number;
  readonly start: PlacementPosition;
}

/**
 * 끄는 동안의 미리보기 — 1mm 로 맞춘 사각형, 또는 너무 작아 만들 수 없는 사유와 표면 안으로 자른 두 점의 사각형
 * (작게 끄는 동안에도 끈 자리를 보이려고 맞추지 않은 채 둔다).
 */
export type DrawPreview =
  | { readonly kind: DrawAreaKind; readonly ok: true; readonly rect: Rect }
  | {
      readonly kind: DrawAreaKind;
      readonly ok: false;
      readonly reason: string;
      readonly rect: Rect;
    };

const clamp = (value: number, max: number): number => Math.min(Math.max(value, 0), max);

/** 두 점을 표면 실측 안으로 자른 외접 사각형(스냅 없음). */
const clampedRectOf = (realSize: SizeMm, a: PlacementPosition, b: PlacementPosition): Rect => {
  const ax = clamp(a.xMm, realSize.widthMm);
  const bx = clamp(b.xMm, realSize.widthMm);
  const ay = clamp(a.yMm, realSize.heightMm);
  const by = clamp(b.yMm, realSize.heightMm);
  return {
    xMm: Math.min(ax, bx),
    yMm: Math.min(ay, by),
    widthMm: Math.abs(bx - ax),
    heightMm: Math.abs(by - ay),
  };
};

const TYPING_TAGS: readonly string[] = ['INPUT', 'TEXTAREA', 'SELECT'];

/**
 * 사각형·원형 도구 — 사진 위를 눌러 끌었다 놓으면 두 점을 대각 모서리로 하는 표면 mm 사각형을 만든다.
 * 끄는 동안의 점은 이 훅의 로컬 상태라 편집 상태(store)에 들어가지 않는다. Shift 는 정사각형·원, Esc 는 취소다.
 */
export const useRectDrawPointer = ({
  svgRef,
  photoSize,
  surface,
  kind,
  onCreate,
  onCancel,
}: Options) => {
  const drag = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<DrawPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active = kind !== null && surface !== undefined;

  const cancel = useCallback((): void => {
    const pointerId = drag.current?.pointerId;
    drag.current = null;
    setPreview(null);
    if (pointerId !== undefined && svgRef.current?.hasPointerCapture(pointerId))
      svgRef.current.releasePointerCapture(pointerId);
  }, [svgRef]);

  useEffect(() => {
    if (!active) {
      cancel();
      setError(null);
    }
  }, [active, cancel]);

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      if (target instanceof HTMLElement && TYPING_TAGS.includes(target.tagName)) return;
      if (event.key !== 'Escape') return;
      cancel();
      setError(null);
      onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, cancel, onCancel]);

  /** 포인터 이벤트의 표면 mm 점. svg·화면 행렬이 없으면 null. */
  const mmOf = (event: PointerEvent<SVGElement>): PlacementPosition | null => {
    if (surface === undefined || svgRef.current === null) return null;
    const point = clientToPhotoPoint(svgRef.current, { x: event.clientX, y: event.clientY });
    return point === null ? null : surfacePhotoToMm(surface, photoSize, point);
  };
  const previewAt = (event: PointerEvent<SVGElement>): DrawPreview | null => {
    const current = drag.current;
    if (kind === null || surface === undefined || current === null) return null;
    if (event.pointerId !== current.pointerId) return null;
    const to = mmOf(event);
    if (to === null) return null;
    const result = rectFromSurfacePoints(surface.realSize, current.start, to, {
      square: event.shiftKey,
    });
    return result.ok
      ? { kind, ok: true, rect: result.rect }
      : {
          kind,
          ok: false,
          reason: result.reason,
          rect: clampedRectOf(surface.realSize, current.start, to),
        };
  };

  const start = (event: PointerEvent<SVGElement>): void => {
    if (!active || event.button !== 0 || svgRef.current === null) return;
    if (photoSize.widthPx <= 0 || photoSize.heightPx <= 0) return;
    const point = mmOf(event);
    if (point === null) return;
    event.preventDefault();
    drag.current = { pointerId: event.pointerId, start: point };
    setError(null);
    svgRef.current.setPointerCapture(event.pointerId);
  };
  const move = (event: PointerEvent<SVGElement>): void => {
    const next = previewAt(event);
    if (next !== null) setPreview(next);
  };
  const finish = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return;
    const result = previewAt(event) ?? preview;
    cancel();
    if (result === null || kind === null) return;
    if (!result.ok) {
      setError(result.reason);
      return;
    }
    setError(onCreate(kind, result.rect));
  };

  return { preview, error, start, move, finish, cancel };
};
